import { parseMysqlTime } from './format';
import type { BetBundle } from '@/types';

/**
 * Shared bet-level derivations.
 *
 * Both the single-bet verdict and the two-bet comparison need the same answers —
 * "what did it pay, how did it end, how long did it take" — computed the same
 * way. Deriving them once here keeps Bet Inspection and Compare from ever
 * disagreeing about the same bet.
 */

export type Outcome = 'won' | 'lost' | 'rolled-back' | 'open' | 'unknown';

/**
 * Derived from the transactions themselves, never a game-specific status column:
 * the per-game `bets` schema differs per game, so its status field cannot be
 * relied on generically.
 */
export function deriveOutcome(trx: BetBundle['transactions']['data']): Outcome {
  if (!trx.length) return 'unknown';
  const hasBet = trx.some((t) => t.action === 'bet');
  const wins = trx.filter((t) => t.action === 'win');
  const rolled = trx.some((t) => t.action === 'rollback') || trx.some((t) => t.rollbacked === 1);

  if (rolled) return 'rolled-back';
  if (wins.length) return wins.some((w) => Number(w.amount) > 0) ? 'won' : 'lost';
  if (hasBet) return 'open';
  return 'unknown';
}

export interface BetSummary {
  betId: string;
  playServiceId: string;
  cycleUniqId: string;
  gameUuid: string;
  gameName: string | null;
  gameType: string | null;
  currency: string | null;
  player: string | null;
  /** Bet stake (the `bet` transaction's amount). */
  stake: number | null;
  /** Sum of all `win` amounts. */
  payout: number;
  /** payout − stake, when a stake exists. */
  net: number | null;
  rollbacks: number;
  txCount: number;
  sessions: number;
  outcome: Outcome;
  /** Wall-clock span between the first and last transaction, in ms. */
  spanMs: number | null;
  firstAt: Date | null;
}

export function summarizeBet(bundle: BetBundle): BetSummary {
  const trx = bundle.transactions.data ?? [];
  const bet = trx.find((t) => t.action === 'bet');
  const wins = trx.filter((t) => t.action === 'win');
  const rollbacks = trx.filter((t) => t.action === 'rollback');

  const stake = bet?.amount != null && bet.amount !== '' ? Number(bet.amount) : null;
  const payout = wins.reduce((sum, w) => sum + Number(w.amount ?? 0), 0);

  const ms = trx
    .map((t) => parseMysqlTime(t.created_at))
    .filter((d): d is Date => Boolean(d))
    .map((d) => d.getTime());
  const spanMs = ms.length > 1 ? Math.max(...ms) - Math.min(...ms) : null;
  const firstAt = ms.length ? new Date(Math.min(...ms)) : null;

  const game = bundle.resolved.game;
  const playerRow = trx.find((t) => t.player_id);

  return {
    betId: bundle.resolved.bet_id,
    playServiceId: bundle.resolved.play_service_id,
    cycleUniqId: bundle.resolved.cycle_uniq_id,
    gameUuid: bundle.resolved.game_uuid,
    gameName: game?.name != null ? String(game.name) : null,
    gameType: game?.type != null ? String(game.type) : null,
    currency: bet?.currency ?? wins[0]?.currency ?? null,
    player: playerRow?.player_id != null ? String(playerRow.player_id) : null,
    stake,
    payout,
    net: stake != null ? payout - stake : null,
    rollbacks: rollbacks.length,
    txCount: trx.length,
    sessions: bundle.sessions.data?.length ?? 0,
    outcome: deriveOutcome(trx),
    spanMs,
    firstAt,
  };
}
